/**
 * `/agenda` — agenda public de l'établissement.
 *
 * Remplace le calendrier mensuel : il n'affichait que le blob agenda.json (donc
 * jamais les événements créés par le staff), imposait de deviner sur quelle case
 * taper pour voir s'il se passait quelque chose, et ne menait nulle part. La
 * liste chronologique groupée par mois répond à la seule question posée — « qu'y
 * a-t-il de prévu ? » — et chaque ligne ouvre la page événement partageable.
 *
 * Route publique : aucune auth, aucun compte requis.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MaskIcon } from '../components/Icon';
import {
  fetchPublicEvents,
  groupByMonth,
  dateBadge,
  formatDayLong,
  formatTimeRange,
  formatPrice,
  type PublicEvent,
} from '../utils/events';
import { getEventTypeStyle } from '../utils/eventTypes';
import './AgendaScreen.css';

/** Bloc partagé par « aucun événement du tout » et « ce mois-ci est vide ». */
function AgendaEmpty() {
  return (
    <div className="agenda-empty">
      {/* Icône du jeu FontAwesome de l'app, pas un emoji : le rendu est
          identique sur tous les OS et ça reste neutre pour tous les tenants
          (un verre de vin jurerait sur Nodo, bar à cocktails). */}
      <MaskIcon name="calendar" size={28} color="var(--color-text-tertiary)" />
      <p>Rien à l'agenda pour l'instant. Reviens bientôt !</p>
    </div>
  );
}

export function AgendaScreen() {
  const navigate = useNavigate();
  const [events, setEvents] = useState<PublicEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPublicEvents().then(setEvents).finally(() => setLoading(false));
  }, []);

  const groups = useMemo(() => groupByMonth(events), [events]);

  return (
    <div className="agenda">
      {/* Le titre porte la safe-area : l'écran n'a plus de barre pleine en haut,
          donc sans ce padding il passerait sous le notch (bug constaté en PWA
          installée, où le status bar est translucide). */}
      <header className="agenda-head">
        <button className="agenda-back" onClick={() => navigate('/')} aria-label="Retour à l'accueil">
          <MaskIcon name="chevron-left" size={16} color="var(--color-text-secondary)" />
        </button>
        <h1 className="agenda-title">Événements</h1>
      </header>

      {loading && <p className="agenda-status">Chargement…</p>}

      {!loading && events.length === 0 && <AgendaEmpty />}

      {groups.map((group) => (
        <section className="agenda-month" key={group.label}>
          {/* Filet + label small caps : le mois se lit au scan sans voler la
              vedette au titre de page. */}
          <div className="agenda-month-head">
            <span className="agenda-month-label">{group.label}</span>
            <span className="agenda-month-rule" />
          </div>

          {group.events.length === 0 ? (
            <AgendaEmpty />
          ) : (
            group.events.map((evt) => {
              const badge = dateBadge(evt.date);
              const style = getEventTypeStyle(evt.type);
              const when = formatTimeRange(evt.startTime, evt.endTime);
              return (
                <button
                  key={evt.id}
                  className="agenda-card"
                  onClick={() => navigate(`/evenement/${encodeURIComponent(evt.id)}`)}
                >
                  {/* Vignette carrée : la cover si elle existe, sinon une pastille
                      date sur la couleur du type — jamais un trou gris. Le texte
                      hérite de `fg` : sur les fonds pastel, du blanc serait illisible. */}
                  <span
                    className="agenda-thumb"
                    style={
                      evt.coverImageUrl
                        ? { backgroundImage: `url(${evt.coverImageUrl})` }
                        : { background: style.bg, color: style.fg }
                    }
                  >
                    {!evt.coverImageUrl && (
                      <>
                        <span className="agenda-thumb-day">{badge.day}</span>
                        <span className="agenda-thumb-month">{badge.month}</span>
                      </>
                    )}
                  </span>
                  <span className="agenda-card-body">
                    <span className="agenda-card-title">{evt.title}</span>
                    <span className="agenda-card-when">
                      {formatDayLong(evt.date)}
                      {when ? ` · ${when}` : ''}
                    </span>
                    <span className="agenda-card-meta">
                      <span
                        className="agenda-card-type"
                        style={{ background: style.bg, color: style.fg }}
                      >
                        {style.label}
                      </span>
                      <span className="agenda-card-price">{formatPrice(evt.price)}</span>
                    </span>
                  </span>
                  <MaskIcon
                    className="agenda-card-chevron"
                    name="chevron-right"
                    size={14}
                    color="var(--color-text-tertiary)"
                  />
                </button>
              );
            })
          )}
        </section>
      ))}
    </div>
  );
}
